"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { FleissSummary } from "@/components/tools/stats-research/fleiss-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { PreviewTable } from "@/components/tools/stats-research/preview-table";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  countsFromRatings,
  defaultCategoryOrder,
  ratersPerSubject,
} from "@/lib/tool-kit/stats-research/agreement";
import {
  type HeaderMode,
  parseLabelTable,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const FLEISS_KAPPA_CALCULATOR_STRINGS = {
  modeLabel: "Your data",
  modeCounts: "Counts per category",
  modeRatings: "One rating per cell",
  countsLabel: "Counts — one row per subject, one column per category",
  countsHint:
    "Each cell is how many raters put that subject in that category, so every row adds up to the number of raters. Subject names in the first column and category names in a header row are optional.",
  countsPlaceholder: "\tYes\tNo\nItem 1\t3\t1\nItem 2\t0\t4\nItem 3\t2\t2",
  countsExample:
    "\tPositive\tNeutral\tNegative\nPost 1\t4\t0\t0\nPost 2\t3\t1\t0\nPost 3\t0\t4\t0\nPost 4\t0\t3\t1\nPost 5\t1\t1\t2\nPost 6\t0\t0\t4\nPost 7\t2\t2\t0\nPost 8\t0\t1\t3\nPost 9\t4\t0\t0\nPost 10\t0\t4\t0",
  ratingsLabel: "Ratings — one row per subject, one column per rater",
  ratingsHint:
    "Category labels or codes, pasted from a spreadsheet — leave out any ID column. Every subject needs the same number of ratings; rows with a different number are left out.",
  ratingsPlaceholder: "Coder 1\tCoder 2\tCoder 3\nyes\tyes\tno\nno\tno\tno",
  ratingsExample:
    "Coder 1\tCoder 2\tCoder 3\tCoder 4\nPositive\tPositive\tPositive\tPositive\nPositive\tPositive\tNeutral\tPositive\nNeutral\tNeutral\tNeutral\tNeutral\nNeutral\tNegative\tNeutral\tNeutral\nPositive\tNeutral\tNegative\tNegative\nNegative\tNegative\tNegative\tNegative\nPositive\tNeutral\tPositive\tNeutral\nNegative\tNegative\tNeutral\tNegative\nPositive\tPositive\tPositive\tPositive\nNeutral\tNeutral\tNeutral\tNeutral",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  categoryName: "Category {number}",
  subjectName: "Subject {number}",
  raters: "n",
  subjectCount: "one: {count} subject|other: {count} subjects",
  raterCount: "one: {count} rater|other: {count} raters",
  categoryCount: "one: {count} category|other: {count} categories",
  readSummary: "{subjects}, {raters} each, {categories}.",
  moreRows:
    "one: … and {count} more subject|other: … and {count} more subjects",
  notCounts:
    "Every cell must be a whole number of raters, zero or more. Check line {line}.",
  unequalRaters:
    "Every subject must be rated by the same number of raters. Most rows add up to {n}; these lines do not: {lines}.",
  droppedRatings:
    "one: {count} subject with a different number of ratings was left out.|other: {count} subjects with a different number of ratings were left out.",
  results: "Results",
  kappa: "Fleiss' κ",
  meanAgreement: "Observed agreement (P̄)",
  chanceAgreement: "Chance agreement (P̄ₑ)",
  seNull: "SE₀ (H₀: κ = 0)",
  zLabel: "z",
  pLabel: "p (two-sided)",
  interpretation:
    "On Landis and Koch's benchmarks, κ = {kappa} is {band} agreement.",
  bandPoor: "poor",
  bandSlight: "slight",
  bandFair: "fair",
  bandModerate: "moderate",
  bandSubstantial: "substantial",
  bandAlmostPerfect: "almost perfect",
  seNote:
    "The standard error is Fleiss, Nee and Landis's (1979) formula under H₀: κ = 0. It is right for the z test but not for a confidence interval, so no interval is shown; for one, bootstrap over subjects.",
  categoryHeading: "Agreement by category",
  columnCategory: "Category",
  columnShare: "Share of ratings (pⱼ)",
  columnKappa: "κⱼ",
  columnZ: "z",
  columnP: "p",
  categoryNote:
    "Each κⱼ treats one category against all the others combined. Its SE₀ is √(2 / (N·n·(n − 1))) = {se}.",
  undefinedCategory: "unused",
  howCalculated: "How this was calculated",
  workingSubject:
    "Pᵢ = (Σⱼ nᵢⱼ² − n) / (n(n − 1)) for each subject; P̄ = their mean = {pBar}",
  workingChance: "P̄ₑ = Σⱼ pⱼ² = {terms} = {peBar}",
  workingKappa:
    "κ = (P̄ − P̄ₑ) / (1 − P̄ₑ) = ({pBar} − {peBar}) / (1 − {peBar}) = {kappa}",
  workingZ: "z = κ / SE₀ = {kappa} / {se} = {z}",
  report: "Fleiss' κ = {kappa}, z = {z}, p {p} ({subjects}, {raters} each).",
  copyResult: "Copy APA-style result",
  copied: "Copied!",
  enterPrompt:
    "Paste a table of counts, or one row of ratings per subject, to see Fleiss' kappa.",
  degenerate:
    "Kappa is undefined here: every rating falls in the same category, so chance agreement is 100%.",
  tooFew:
    "Fleiss' kappa needs at least two raters per subject and at least two categories.",
} as const;

type Strings = typeof FLEISS_KAPPA_CALCULATOR_STRINGS;
type Mode = "counts" | "ratings";
type Plural = (key: keyof Strings, count: number) => string;

interface CountTable {
  counts: number[][];
  categories: string[];
  subjects: string[];
  problem: string | null;
  flagged: Set<number>;
  notes: string[];
}

function nameList(
  count: number,
  names: readonly (string | undefined)[] | null | undefined,
  template: string
): string[] {
  return Array.from(
    { length: count },
    (_, i) => names?.[i] || t(template, { number: String(i + 1) })
  );
}

function fromCounts(
  text: string,
  header: HeaderMode,
  strings: Strings,
  fmt: (value: number) => string
): CountTable {
  const parsed = parseNumericTable(text, header);
  const base: CountTable = {
    counts: [],
    categories: nameList(parsed.columns, parsed.header, strings.categoryName),
    subjects: nameList(
      parsed.rows.length,
      parsed.rowLabels,
      strings.subjectName
    ),
    problem: null,
    flagged: new Set(),
    notes: [],
  };
  const bad = parsed.rows.findIndex((row) =>
    row.some((cell) => cell === null || !Number.isInteger(cell) || cell < 0)
  );
  if (bad >= 0) {
    return {
      ...base,
      problem: t(strings.notCounts, { line: fmt(parsed.lines[bad]) }),
    };
  }
  const counts = parsed.rows as number[][];
  const { mismatched } = ratersPerSubject(counts);
  if (mismatched.length > 0) {
    const others = counts.find((_, i) => !mismatched.includes(i)) ?? [];
    return {
      ...base,
      counts,
      flagged: new Set(mismatched),
      problem: t(strings.unequalRaters, {
        n: fmt(others.reduce((sum, value) => sum + value, 0)),
        lines: mismatched.map((i) => fmt(parsed.lines[i])).join(", "),
      }),
    };
  }
  return { ...base, counts };
}

function fromRatings(
  text: string,
  header: HeaderMode,
  strings: Strings,
  plural: Plural
): CountTable {
  const parsed = parseLabelTable(text, header);
  const rated = parsed.rows.map((row) => row.filter((label) => label !== ""));
  const tally = new Map<number, number>();
  for (const row of rated) {
    tally.set(row.length, (tally.get(row.length) ?? 0) + 1);
  }
  const raters = [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0;
  const kept = rated.filter((row) => row.length === raters);
  const categories = defaultCategoryOrder(kept.flat());
  const notes =
    kept.length < rated.length
      ? [plural("droppedRatings", rated.length - kept.length)]
      : [];
  return {
    counts: countsFromRatings(kept, categories),
    categories,
    subjects: nameList(kept.length, null, strings.subjectName),
    problem: null,
    flagged: new Set(),
    notes,
  };
}

export default function FleissKappaCalculatorWidget() {
  const strings = useToolUi(FLEISS_KAPPA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("counts");
  const [countsText, setCountsText] = useState("");
  const [ratingsText, setRatingsText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");

  const table = useMemo(
    () =>
      mode === "counts"
        ? fromCounts(countsText, header, strings, fmt)
        : fromRatings(ratingsText, header, strings, (key, count) =>
            tPlural(strings[key], locale, count, { count: fmt(count) }).trim()
          ),
    [mode, countsText, ratingsText, header, strings, fmt, locale]
  );
  const text = mode === "counts" ? countsText : ratingsText;
  const setText = mode === "counts" ? setCountsText : setRatingsText;
  const hasRows = table.counts.length > 0 && table.categories.length > 0;
  const raters = table.counts[0]?.reduce((sum, value) => sum + value, 0) ?? 0;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="fk-mode"
        onChange={setMode}
        options={[
          { value: "counts", label: strings.modeCounts },
          { value: "ratings", label: strings.modeRatings },
        ]}
        value={mode}
      />
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              setHeader("auto");
              setText(
                mode === "counts"
                  ? strings.countsExample
                  : strings.ratingsExample
              );
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={mode === "counts" ? strings.countsHint : strings.ratingsHint}
        id="fk-data"
        label={mode === "counts" ? strings.countsLabel : strings.ratingsLabel}
        onChange={setText}
        placeholder={
          mode === "counts"
            ? strings.countsPlaceholder
            : strings.ratingsPlaceholder
        }
        rows={9}
        value={text}
      />
      {table.problem ? <ErrorNotice>{table.problem}</ErrorNotice> : null}
      {hasRows ? (
        <section className="space-y-2">
          <p className={HINT}>
            {t(strings.readSummary, {
              subjects: tPlural(
                strings.subjectCount,
                locale,
                table.counts.length,
                {
                  count: fmt(table.counts.length),
                }
              ).trim(),
              raters: tPlural(strings.raterCount, locale, raters, {
                count: fmt(raters),
              }).trim(),
              categories: tPlural(
                strings.categoryCount,
                locale,
                table.categories.length,
                { count: fmt(table.categories.length) }
              ).trim(),
            })}{" "}
            {table.notes.join(" ")}
          </p>
          <PreviewTable
            flagged={table.flagged}
            head={[...table.categories, strings.raters]}
            limit={12}
            more={(hidden) =>
              tPlural(strings.moreRows, locale, hidden, {
                count: fmt(hidden),
              }).trim()
            }
            rowHeads={table.subjects}
            rows={table.counts.map((row) => [
              ...row.map((value) => fmt(value)),
              fmt(row.reduce((sum, value) => sum + value, 0)),
            ])}
          />
        </section>
      ) : null}
      {hasRows && table.problem === null ? (
        <FleissSummary
          categories={table.categories}
          counts={table.counts}
          fmt={fmt}
          locale={locale}
          strings={strings}
        />
      ) : null}
      {hasRows || table.problem ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
